import { useEffect, useRef, useState } from "react";
import api, { PROJECT_STATUS } from "@/lib/api";
import PageHeader, { StatusBadge } from "@/components/PageHeader";
import WorksNav from "@/components/WorksNav";
import ClientPicker from "@/components/ClientPicker";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Sparkles, Loader2, MapPin, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const empty = {
  name: "", code: "", client: "", contracting_entity_id: "", address: "",
  budget: 0, status: "planificacion", progress: 0,
  start_date: "", end_date: "", description: "",
  residente_id: "", supervisor_id: "",
  geofence_lat: "", geofence_lng: "", geofence_radius_m: 200, attendance_cutoff_time: "10:00",
};

export default function Projects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [scanning, setScanning] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoResults, setGeoResults] = useState([]);

  const searchGeo = async () => {
    setGeoLoading(true);
    try {
      const { data } = await api.get(`/geo/search?q=${encodeURIComponent(form.address)}`);
      setGeoResults(data || []);
      if (!data?.length) toast.info("Sin resultados; intenta con calle y ciudad");
    } catch { toast.error("Error al buscar dirección"); }
    setGeoLoading(false);
  };
  const pickGeo = (r) => {
    setForm((f) => ({ ...f, address: r.address, geofence_lat: r.lat.toFixed(6), geofence_lng: r.lng.toFixed(6) }));
    setGeoResults([]);
    toast.success("Dirección y coordenadas de geocerca aplicadas");
  };
  const [scanData, setScanData] = useState(null);
  const [createContractToo, setCreateContractToo] = useState(true);
  const scanRef = useRef(null);
  const canCreateContract = ["admin", "contador", "auxiliar"].includes(user?.role);

  const handleScanContract = async (file) => {
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/contracts/scan-caratula", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 90000,
      });
      const ben = data.beneficiary || {};
      const addr = [ben.locality, ben.municipality, ben.state].filter(Boolean).join(", ");
      const subtotal = Number(data.subtotal) || 0;
      const total = Number(data.total) || (subtotal ? Math.round(subtotal * (1 + (Number(data.iva_pct) || 16) / 100) * 100) / 100 : 0);
      setForm((f) => ({
        ...f,
        name: ben.name || data.description?.slice(0, 80) || f.name,
        client: data.contracting_entity?.name || f.client,
        contracting_entity_id: data.matched_contracting_entity_id || f.contracting_entity_id,
        address: addr || ben.address || f.address,
        budget: total || f.budget,
        start_date: data.start_date || f.start_date,
        end_date: data.end_date || f.end_date,
        description: [data.description, data.contract_number ? `Contrato: ${data.contract_number}` : null].filter(Boolean).join("\n") || f.description,
      }));
      toast.success("Datos del contrato extraídos. Revisa y completa lo que falte.");
      setScanData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo leer el contrato");
    } finally {
      setScanning(false);
      if (scanRef.current) scanRef.current.value = "";
    }
  };

  const canCreate = ["admin", "supervisor"].includes(user?.role);

  const load = async () => {
    const [p, u] = await Promise.all([api.get("/projects"), api.get("/users")]);
    setProjects(p.data);
    setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm(empty); setScanData(null); setCreateContractToo(true); setOpen(true); };
  const startEdit = (p) => {
    setEditing(p.id);
    setForm({ ...empty, ...p, residente_id: p.residente_id || "", supervisor_id: p.supervisor_id || "" });
    setOpen(true);
  };

  const canEditAll = user?.role === "admin";
  const onlyProgress = ["residente", "supervisor"].includes(user?.role);

  const save = async (e) => {
    e.preventDefault();
    let payload;
    if (editing && onlyProgress) {
      // Residente/supervisor: only progress is editable
      payload = { progress: Number(form.progress) };
    } else {
      payload = { ...form, budget: Number(form.budget), progress: Number(form.progress) };
      // Remove read-only / server-managed fields that we shouldn't send back
      ["id", "created_at", "created_by", "updated_at"].forEach((k) => delete payload[k]);
      // Normalize geofence fields
      if (payload.geofence_lat === "" || payload.geofence_lat == null) delete payload.geofence_lat;
      else payload.geofence_lat = Number(payload.geofence_lat);
      if (payload.geofence_lng === "" || payload.geofence_lng == null) delete payload.geofence_lng;
      else payload.geofence_lng = Number(payload.geofence_lng);
      if (payload.geofence_radius_m !== "" && payload.geofence_radius_m != null) payload.geofence_radius_m = Number(payload.geofence_radius_m);
      Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
    }
    try {
      if (editing) await api.patch(`/projects/${editing}`, payload);
      else {
        const { data: newProj } = await api.post("/projects", payload);
        if (scanData && createContractToo && canCreateContract && scanData.contract_number) {
          try {
            const cp = {
              project_id: newProj.id,
              contract_number: scanData.contract_number,
              description: scanData.description || form.name || "Obra",
              modality: scanData.modality || "adjudicacion_directa",
              funding_source: scanData.funding_source || "Privado",
              funding_source_detail: scanData.funding_source_detail || null,
              pgo_number: scanData.pgo_number || null,
              adjudication_reference: scanData.adjudication_reference || null,
              contractor: scanData.contractor || {},
              beneficiary: scanData.beneficiary || {},
              subtotal: scanData.subtotal || 0,
              iva_pct: scanData.iva_pct ?? 16,
              advances: scanData.advances || [],
              payment_form: scanData.payment_form || "estimaciones",
              payment_form_detail: scanData.payment_form_detail || null,
              signing_date: scanData.signing_date || null,
              start_date: scanData.start_date || null,
              end_date: scanData.end_date || null,
            };
            if (scanData.matched_contracting_entity_id) cp.contracting_entity_id = scanData.matched_contracting_entity_id;
            if (scanData.signer_contratante?.name) cp.signer_contratante = scanData.signer_contratante;
            if (scanData.signer_contratista?.name) cp.signer_contratista = scanData.signer_contratista;
            await api.post("/contracts", cp);
            toast.success(`Contrato ${scanData.contract_number} creado en Contratos`);
          } catch (cerr) {
            toast.error(`Proyecto creado, pero el contrato no se pudo crear: ${cerr.response?.data?.detail || "revisa Contratos"}`);
          }
        }
      }
      toast.success(editing ? "Proyecto actualizado" : "Proyecto creado");
      setScanData(null);
      setOpen(false);
      load();
    } catch (err) {
      console.error("Project save error:", err.response?.data || err);
      const detail = err.response?.data?.detail;
      let msg = "Error al guardar";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) {
        // FastAPI validation error format
        msg = detail.map((d) => `${(d.loc || []).join(".")}: ${d.msg}`).join("; ");
      } else if (detail && typeof detail === "object") {
        msg = JSON.stringify(detail);
      }
      toast.error(msg);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este proyecto?")) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Proyecto eliminado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar el proyecto");
    }
    load();
  };

  const residentes = users.filter((u) => u.role === "residente");
  const supervisors = users.filter((u) => u.role === "supervisor");
  const filtered = projects.filter((p) =>
    (!search.trim() || (p.name || "").toLowerCase().includes(search.trim().toLowerCase())) &&
    (!filterStatus || p.status === filterStatus)
  );

  return (
    <div data-testid="projects-page">
      <PageHeader
        subtitle="Obras y desarrollos"
        title="Proyectos"
        actions={
          canCreate && (
            <button data-testid="new-project-btn" onClick={startNew} className="btn-primary px-4 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest">
              <Plus className="w-4 h-4" /> Nuevo proyecto
            </button>
          )
        }
      />
      <div className="px-6 lg:px-8 py-6 space-y-4">
        <WorksNav active="projects" />
        <div className="panel p-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input data-testid="project-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar obra por nombre…" className="input-cyber w-full pl-9 pr-3 py-2 text-sm" />
          </div>
          <div className="flex gap-1 flex-wrap">
            <button data-testid="project-filter-all" onClick={() => setFilterStatus("")}
              className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold rounded-sm transition-colors"
              style={!filterStatus ? { background: "var(--cyan)", color: "#0c1424" } : { color: "var(--text-dim)", border: "1px solid var(--border-2)" }}>
              Todas
            </button>
            {PROJECT_STATUS.map((s) => (
              <button key={s.value} data-testid={`project-filter-${s.value}`} onClick={() => setFilterStatus(filterStatus === s.value ? "" : s.value)}
                className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold rounded-sm transition-colors"
                style={filterStatus === s.value ? { background: "var(--cyan)", color: "#0c1424" } : { color: "var(--text-dim)", border: "1px solid var(--border-2)" }}>
                {s.label}
              </button>
            ))}
          </div>
          {(search || filterStatus) && (
            <span className="text-[10px] font-mono text-slate-500" data-testid="project-filter-count">{filtered.length} de {projects.length}</span>
          )}
        </div>
        <div className="panel overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="projects-table">
            <thead className="panel-header">
              <tr className="text-left">
                <Th>Nombre</Th><Th>Cliente</Th><Th>Estado</Th><Th right>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {projects.length === 0 && (
                <tr><td colSpan={4} className="text-center py-12 text-slate-500">Sin proyectos.</td></tr>
              )}
              {projects.length > 0 && filtered.length === 0 && (
                <tr><td colSpan={4} className="text-center py-12 text-slate-500">Ninguna obra coincide con la búsqueda.</td></tr>
              )}
              {filtered.map((p) => (
                <tr key={p.id} data-testid={`project-row-${p.id}`} className="row-hover transition-colors" style={{ borderBottom: "1px solid var(--border)" }}>
                  <Td>
                    <Link to={`/projects/${p.id}`} data-testid={`project-name-link-${p.id}`} className="font-semibold text-slate-100 hover:text-cyan-300 hover:underline block">{p.name}</Link>
                    <div className="text-xs text-slate-500 truncate max-w-[260px]">{p.address}</div>
                  </Td>
                  <Td className="text-slate-300">{p.client}</Td>
                  <Td><StatusBadge items={PROJECT_STATUS} value={p.status} /></Td>
                  <Td right>
                    <button data-testid={`edit-project-${p.id}`} onClick={() => startEdit(p)} className="text-xs font-semibold hover:underline mr-3" style={{ color: "var(--cyan)" }}>EDITAR</button>
                    {user?.role === "admin" && (
                      <button data-testid={`delete-project-${p.id}`} onClick={() => remove(p.id)} className="text-xs font-semibold hover:underline" style={{ color: "var(--red)" }}>ELIMINAR</button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {open && (
        <Modal onClose={() => setOpen(false)} title={editing ? "Editar proyecto" : "Nuevo proyecto"}>
          <form onSubmit={save} className="space-y-4">
            {!editing && !onlyProgress && (
              <div className="px-3 py-2.5 rounded-sm flex items-center justify-between gap-2 flex-wrap" style={{ background: "rgba(34,211,238,0.06)", border: "1px dashed var(--cyan)" }}>
                <div className="text-xs text-slate-300">
                  <span className="label-mono" style={{ color: "var(--cyan)" }}>// IA</span> Sube el contrato (PDF o foto) y lleno el formulario por ti.
                </div>
                <input ref={scanRef} type="file" accept="image/*,application/pdf" className="hidden" data-testid="project-scan-input"
                  onChange={(e) => handleScanContract(e.target.files?.[0])} />
                <button type="button" data-testid="project-scan-btn" disabled={scanning} onClick={() => scanRef.current?.click()}
                  className="btn-primary px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-50">
                  {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {scanning ? "Leyendo contrato…" : "Escanear contrato"}
                </button>
                {scanData?.contract_number && canCreateContract && (
                  <label className="w-full flex items-center gap-2 text-xs cursor-pointer mt-1" data-testid="create-contract-too-label">
                    <input type="checkbox" checked={createContractToo} onChange={(e) => setCreateContractToo(e.target.checked)}
                      data-testid="create-contract-too-checkbox" className="accent-cyan-400" />
                    <span className="text-slate-300">
                      Crear también el contrato <b className="font-mono" style={{ color: "var(--cyan)" }}>{scanData.contract_number}</b> en Contratos (con montos, anticipos y beneficiario)
                    </span>
                  </label>
                )}
              </div>
            )}
            {editing && onlyProgress && (
              <div className="px-3 py-2 rounded-sm flex items-center gap-2 text-xs" style={{ background: "rgba(34,211,238,0.08)", border: "1px solid var(--cyan)", color: "#67e8f9" }}>
                <span className="label-mono">// PERMISOS</span>
                <span>Como {user.role}, solo puedes actualizar el <strong>Avance %</strong> de la obra.</span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <In label="Código" testId="proj-code" value={form.code} onChange={(v) => setForm({ ...form, code: v })} disabled={editing && onlyProgress} />
              <In label="Nombre" testId="proj-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} disabled={editing && onlyProgress} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-mono block mb-1.5">Cliente del catálogo (opcional)</label>
                <ClientPicker
                  value={form.contracting_entity_id || null}
                  onChange={async (id) => {
                    if (!id) {
                      setForm({ ...form, contracting_entity_id: "" });
                      return;
                    }
                    try {
                      const { data } = await api.get(`/contracting-entities/${id}`);
                      setForm({
                        ...form,
                        contracting_entity_id: id,
                        client: data.name || form.client,
                      });
                    } catch {
                      setForm({ ...form, contracting_entity_id: id });
                    }
                  }}
                  label=""
                  placeholder="Vincular con cliente del catálogo…"
                  allowEmpty
                />
              </div>
              <In label="Cliente (texto libre)" testId="proj-client" value={form.client} onChange={(v) => setForm({ ...form, client: v })} disabled={editing && onlyProgress} />
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div className="col-span-2">
                <label className="label-mono block mb-1.5">Dirección</label>
                <div className="flex gap-2">
                  <input data-testid="proj-address" value={form.address} onChange={(v) => setForm({ ...form, address: v.target.value })}
                    disabled={editing && onlyProgress} placeholder="Escribe y busca en el mapa…" className="input-cyber px-3 py-2 text-sm w-full" />
                  <button type="button" data-testid="geo-search-btn" disabled={geoLoading || (form.address || "").length < 3}
                    onClick={searchGeo} title="Buscar dirección en el mapa"
                    className="btn-primary px-3 py-2 text-xs uppercase tracking-widest disabled:opacity-50 whitespace-nowrap">
                    {geoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                  </button>
                </div>
                {geoResults.length > 0 && (
                  <div className="panel mt-1 max-h-44 overflow-y-auto" data-testid="geo-results">
                    {geoResults.map((r, i) => (
                      <button type="button" key={i} data-testid={`geo-result-${i}`} onClick={() => pickGeo(r)}
                        className="block w-full text-left px-3 py-2 text-xs hover:bg-white/5" style={{ borderBottom: "1px solid var(--border)" }}>
                        <div className="text-slate-200">{r.address}</div>
                        <div className="text-[10px] text-slate-500 truncate">{r.display}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <In label="Presupuesto MXN" testId="proj-budget" type="number" value={form.budget} onChange={(v) => setForm({ ...form, budget: v })} disabled={editing && onlyProgress} />
              <In label="Avance %" testId="proj-progress" type="number" value={form.progress} onChange={(v) => setForm({ ...form, progress: v })} />
              <Sel label="Estado" testId="proj-status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={PROJECT_STATUS} disabled={editing && onlyProgress} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <In label="Inicio" testId="proj-start" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} required={false} disabled={editing && onlyProgress} />
              <In label="Fin" testId="proj-end" type="date" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} required={false} disabled={editing && onlyProgress} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Sel label="Residente" testId="proj-residente" value={form.residente_id} onChange={(v) => setForm({ ...form, residente_id: v })}
                options={[{ value: "", label: "—" }, ...residentes.map((u) => ({ value: u.id, label: u.name }))]} disabled={editing && onlyProgress} />
              <Sel label="Supervisor" testId="proj-supervisor" value={form.supervisor_id} onChange={(v) => setForm({ ...form, supervisor_id: v })}
                options={[{ value: "", label: "—" }, ...supervisors.map((u) => ({ value: u.id, label: u.name }))]} disabled={editing && onlyProgress} />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Descripción</label>
              <textarea data-testid="proj-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} disabled={editing && onlyProgress}
                className="w-full input-cyber px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed" />
            </div>

            {/* Geofence — for time-clock validation */}
            <div className="panel p-3 space-y-3" style={{ background: "rgba(8,12,24,0.6)" }}>
              <div className="flex items-center justify-between">
                <span className="label-mono" style={{ color: "var(--cyan)" }}>// GEOCERCA DE OBRA</span>
                <button type="button"
                  data-testid="proj-geofence-here"
                  disabled={editing && onlyProgress}
                  onClick={() => {
                    if (!navigator.geolocation) return;
                    navigator.geolocation.getCurrentPosition((pos) => {
                      setForm({ ...form, geofence_lat: pos.coords.latitude.toFixed(6), geofence_lng: pos.coords.longitude.toFixed(6) });
                    });
                  }}
                  className="text-[10px] label-mono hover:underline disabled:opacity-40" style={{ color: "var(--magenta)" }}>
                  USAR MI UBICACIÓN
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <In label="Latitud" testId="proj-geo-lat" type="number" value={form.geofence_lat} onChange={(v) => setForm({ ...form, geofence_lat: v })} required={false} disabled={editing && onlyProgress} />
                <In label="Longitud" testId="proj-geo-lng" type="number" value={form.geofence_lng} onChange={(v) => setForm({ ...form, geofence_lng: v })} required={false} disabled={editing && onlyProgress} />
              </div>
              {form.geofence_lat && form.geofence_lng && !isNaN(Number(form.geofence_lat)) && !isNaN(Number(form.geofence_lng)) && (
                <div data-testid="proj-map-preview" className="rounded-sm overflow-hidden" style={{ border: "1px solid var(--border)" }}>
                  <iframe
                    title="Ubicación de la obra"
                    className="w-full h-48 block"
                    style={{ filter: "invert(0.88) hue-rotate(180deg) saturate(0.7)", border: 0 }}
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${Number(form.geofence_lng) - 0.004},${Number(form.geofence_lat) - 0.0025},${Number(form.geofence_lng) + 0.004},${Number(form.geofence_lat) + 0.0025}&layer=mapnik&marker=${Number(form.geofence_lat)},${Number(form.geofence_lng)}`}
                  />
                  <div className="flex items-center justify-between px-2 py-1" style={{ background: "rgba(8,12,24,0.8)" }}>
                    <span className="text-[10px] text-slate-500">📍 {Number(form.geofence_lat).toFixed(6)}, {Number(form.geofence_lng).toFixed(6)}</span>
                    <a href={`https://www.openstreetmap.org/?mlat=${form.geofence_lat}&mlon=${form.geofence_lng}#map=17/${form.geofence_lat}/${form.geofence_lng}`}
                      target="_blank" rel="noreferrer" data-testid="proj-map-open-link"
                      className="text-[10px] label-mono hover:underline" style={{ color: "var(--cyan)" }}>VER MAPA COMPLETO</a>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <In label="Radio (m)" testId="proj-geo-radius" type="number" value={form.geofence_radius_m} onChange={(v) => setForm({ ...form, geofence_radius_m: v })} required={false} disabled={editing && onlyProgress} />
                <In label="Hora límite entrada (HH:MM)" testId="proj-cutoff" value={form.attendance_cutoff_time} onChange={(v) => setForm({ ...form, attendance_cutoff_time: v })} required={false} disabled={editing && onlyProgress} />
              </div>
              <p className="text-[11px] text-slate-500">Si configuras la geocerca, el sistema marcará como sospechosa cualquier asistencia hecha fuera del radio. La hora límite genera alertas de retardo.</p>
            </div>

            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" data-testid="save-project-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Guardar</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

const Th = ({ children, right }) => (
  <th className={`label-mono px-4 py-3 ${right ? "text-right" : ""}`}>{children}</th>
);
const Td = ({ children, right, className = "" }) => (
  <td className={`px-4 py-3 align-top ${right ? "text-right" : ""} ${className}`}>{children}</td>
);

export function Modal({ children, onClose, title, footer }) {
  return (
    <div className="fixed inset-0 z-50 p-4 flex items-center justify-center" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div
        className="panel-strong w-full max-w-2xl scan-line flex flex-col"
        style={{ maxHeight: "calc(100dvh - 2rem)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="panel-header px-6 py-4 flex items-center justify-between flex-shrink-0 z-10">
          <div>
            <div className="label-mono">// Formulario</div>
            <h2 className="font-display font-bold text-xl text-slate-100 tracking-wide">{title}</h2>
          </div>
          <button onClick={onClose} data-testid="modal-close" className="text-slate-500 hover:text-cyan-300 transition-colors"><X className="w-5 h-5" /></button>
        </div>
        <div
          className="p-6 overflow-y-auto flex-1"
          style={{ paddingBottom: footer ? "1.5rem" : "calc(1.5rem + env(safe-area-inset-bottom))" }}
        >
          {children}
        </div>
        {footer && (
          <div
            className="flex-shrink-0 px-6 py-3 flex gap-2 justify-end"
            style={{
              borderTop: "1px solid var(--border)",
              background: "#0a0f1d",
              paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))",
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export function In({ label, value, onChange, type = "text", testId, required = true, disabled = false, step }) {
  return (
    <div>
      <label className="label-mono block mb-1.5">{label}</label>
      <input type={type} required={required} disabled={disabled} data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        step={type === "number" ? (step || "any") : undefined}
        className="w-full input-cyber px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed" />
    </div>
  );
}

export function Sel({ label, value, onChange, options, testId, disabled = false }) {
  return (
    <div>
      <label className="label-mono block mb-1.5">{label}</label>
      <select data-testid={testId} value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value)}
        className="w-full input-cyber px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
        {options.map((o) => (
          <option key={o.value} value={o.value} style={{ background: "#0a0f1d", color: "#e5e7eb" }}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}
